import React from 'react'
import { createPortal } from 'react-dom'
import { uniqueId } from 'lodash-es'
import { gsap } from 'gsap'
import classNames from 'classnames'

interface IProps {
  className?: string
  render: (close: () => Promise<unknown>) => React.ReactElement
  activator?: (open: () => Promise<unknown>) => React.ReactElement
  value?: boolean
  onChange?: (value: boolean) => void
  filled?: boolean
}

function Dialog(props: IProps): React.ReactElement {
  const [id] = React.useState(uniqueId('dialog-'))
  const dialogRef = React.useRef<HTMLDivElement>(null)
  const overlayRef = React.useRef(null)
  const contentRef = React.useRef(null)

  const keyUpHandler = (e: any): void => {
    if (e.key === 'Escape') {
      void close()
    }
  }

  const clickHandler = (e: any): void => {
    if (!(e.target as HTMLElement).closest(`#${id} .dialog__content`)) {
      void close()
    }
  }

  const open = async (persistent: boolean = false): Promise<unknown> =>
    await new Promise((resolve) => {
      props.onChange?.(true)

      const dialog = dialogRef.current
      const overlay = overlayRef.current
      const content = contentRef.current

      if (!dialog || !overlay || !content) {
        resolve({})
        return
      }

      const tl = gsap.timeline({
        onComplete: () => {
          window.addEventListener('keyup', keyUpHandler)
          dialogRef.current?.addEventListener('click', clickHandler)

          resolve({})
        }
      })
      tl.set(dialog, { display: 'block' })
      tl.set([overlay, content], { willChange: 'opacity, transform' })
      tl.to(
        overlay,
        {
          opacity: props.filled ? 1.0 : 0.4,
          y: 0,
          duration: props.filled ? 0.15 : 0.35
        },
        '>0.15'
      )
      tl.to(
        content,
        { y: 0, opacity: 1, duration: props.filled ? 0.15 : 0.35 },
        '<'
      )
      tl.set([overlay, content], { willChange: 'auto' })

      if (persistent) {
        tl.totalProgress(1)
      }
    })

  const close = async (persistent: boolean = false): Promise<unknown> =>
    await new Promise((resolve) => {
      props.onChange?.(false)

      const dialog = dialogRef.current
      const overlay = overlayRef.current
      const content = contentRef.current

      if (!dialog || !overlay || !content) {
        resolve({})
        return
      }

      const tl = gsap.timeline({
        onComplete: () => {
          window.removeEventListener('keyup', keyUpHandler)
          dialogRef.current?.removeEventListener('click', clickHandler)

          resolve({})
        }
      })
      tl.set([overlay, content], { willChange: 'opacity, transform' })
      tl.to(
        overlay,
        {
          opacity: 0,
          y: props.filled ? 50 : 0,
          duration: props.filled ? 0.15 : 0.35
        },
        '>0.15'
      )
      tl.to(
        content,
        { y: 50, opacity: 0, duration: props.filled ? 0.15 : 0.35 },
        '<'
      )
      tl.set([overlay, content], { willChange: 'auto' })
      tl.set(dialog, { display: 'none' })

      if (persistent) {
        tl.totalProgress(1)
      }
    })

  React.useEffect(() => {
    if (props.value === true) {
      void open()
    } else if (props.value === false) {
      void close()
    }

    return (): void => {
      window.removeEventListener('keyup', keyUpHandler)
      dialogRef.current?.removeEventListener('click', clickHandler)
    }
  }, [props.value])

  return (
    <div className={`dialog-wrapper ${props.className ?? ''}`.trim()}>
      {props.activator ? props.activator?.(open) : null}
      {createPortal(
        <div
          className={classNames('dialog', { _filled: props.filled })}
          ref={dialogRef}
          id={id}
        >
          <div className="dialog__overlay" ref={overlayRef}></div>
          <div className="dialog__outer">
            <div className="container">
              <div className="dialog__inner">
                <div className="dialog__content" ref={contentRef}>
                  {props.render(close)}
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

export default Dialog
